import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Wheat, Menu, X, ChevronDown, Lock, User as UserIcon } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const LinkItem = ({ to, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="font-sans text-sm tracking-wide text-stone-700 hover:text-emerald-800 transition-colors"
  >
    {label}
  </Link>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <header className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-5 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-emerald-800 flex items-center justify-center">
              <Wheat size={20} className="text-stone-50" />
            </span>
            <div className="leading-tight">
              <div className="font-serif text-xl text-stone-900">Azure Gypsy Farms</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Real Food · Real Farmers</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            <LinkItem to="/marketplace" label="Marketplace" testid="nav-marketplace" />
            <LinkItem to="/hobby-farmers" label="Hobby Farmers" testid="nav-hobby-farmers" />
            <LinkItem to="/drivers" label="Drive With Us" testid="nav-drivers" />
            <LinkItem to="/pricing" label="Membership" testid="nav-pricing" />
            <LinkItem to="/contact" label="Contact" testid="nav-contact" />
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <NotificationBell />
                <Button
                  variant="ghost"
                  data-testid="dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                  className="text-stone-700 hover:text-emerald-800"
                >
                  {user.name?.split(" ")[0] || "Dashboard"}
                </Button>
                <Button
                  data-testid="logout-btn"
                  onClick={logout}
                  variant="outline"
                  className="border-stone-300"
                >
                  Logout
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="signin-menu-btn"
                    className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 rounded-md gap-2"
                  >
                    Sign In
                    <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  data-testid="signin-menu-content"
                  className="w-64 bg-white border border-stone-200"
                >
                  <DropdownMenuLabel className="font-sans text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    Choose your portal
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="signin-menu-member"
                    onClick={() => navigate("/login")}
                    className="cursor-pointer py-3"
                  >
                    <UserIcon size={16} className="mr-3 text-emerald-800" />
                    <div className="flex flex-col">
                      <span className="font-serif text-base text-stone-900">Member sign-in</span>
                      <span className="text-xs font-sans text-stone-500">Farmers, drivers, clients, wholesale</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="signin-menu-admin"
                    onClick={() => navigate("/admin/login")}
                    className="cursor-pointer py-3"
                  >
                    <Lock size={16} className="mr-3 text-amber-700" />
                    <div className="flex flex-col">
                      <span className="font-serif text-base text-stone-900">Admin sign-in</span>
                      <span className="text-xs font-sans text-stone-500">Staff portal · audit-logged</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-stone-700"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden px-6 pb-6 flex flex-col gap-4 border-t border-stone-200 bg-stone-50">
            <Link onClick={() => setOpen(false)} to="/marketplace" className="font-sans text-stone-700">Marketplace</Link>
            <Link onClick={() => setOpen(false)} to="/hobby-farmers" className="font-sans text-stone-700">Hobby Farmers</Link>
            <Link onClick={() => setOpen(false)} to="/drivers" className="font-sans text-stone-700">Drive With Us</Link>
            <Link onClick={() => setOpen(false)} to="/pricing" className="font-sans text-stone-700">Membership</Link>
            <Link onClick={() => setOpen(false)} to="/contact" className="font-sans text-stone-700">Contact</Link>
            {user ? (
              <>
                <Link onClick={() => setOpen(false)} to="/dashboard" className="font-sans text-emerald-800">Dashboard</Link>
                <button onClick={logout} className="text-left font-sans text-stone-700">Logout</button>
              </>
            ) : (
              <>
                <Link onClick={() => setOpen(false)} to="/login" className="font-sans text-emerald-800">Member sign-in</Link>
                <Link onClick={() => setOpen(false)} to="/admin/login" data-testid="mobile-admin-signin" className="font-sans text-amber-700">Admin sign-in</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-stone-200 bg-stone-100 mt-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="font-serif text-xl text-stone-900">Azure Gypsy Farms</div>
            <p className="mt-3 text-sm text-stone-600 font-sans max-w-xs">
              Real food, real hobby farmers, straight to your door. Built on trust, soil, and good neighbors.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Explore</div>
            <ul className="mt-4 space-y-2 font-sans text-sm text-stone-700">
              <li><Link to="/marketplace">Marketplace</Link></li>
              <li><Link to="/pricing">Membership</Link></li>
              <li><Link to="/faq">FAQ & Support</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Join</div>
            <ul className="mt-4 space-y-2 font-sans text-sm text-stone-700">
              <li><Link to="/login">Sign Up as Hobby Farmer</Link></li>
              <li><Link to="/drivers">Drive With Us</Link></li>
              <li><Link to="/wholesale">Wholesale Onboarding</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-stone-200 py-6 text-center text-xs text-stone-500 font-sans">
          © {new Date().getFullYear()} Azure Gypsy Farms. Grown with care.
          <span className="mx-2 text-stone-300">·</span>
          <Link to="/admin/login" data-testid="footer-admin-link" className="text-stone-400 hover:text-emerald-800">
            Staff
          </Link>
        </div>
      </footer>
    </div>
  );
}
